import {
  ArrowUpDown,
  Bookmark,
  BookmarkPlus,
  Braces,
  Brain,
  CircleAlert,
  CircleCheck,
  Combine,
  Compass,
  Database,
  Gauge,
  GitBranch,
  Link2,
  LoaderCircle,
  MessageSquare,
  Network,
  Route,
  Search,
  SkipForward,
  Tags,
  Type,
  Workflow,
} from "lucide-react";
import type { JSX } from "react";
import { type ComponentType, type SVGProps, useEffect, useRef, useState } from "react";

import {
  type ReasoningLevel,
  type StepCall,
  type StepState,
  type StepUsage,
  type ToolCall,
  describeTool,
  parentOf,
} from "@/lib/chat";
import { formatCost } from "@/lib/format-cost";
import { readStepperExpandedPref, writeStepperExpandedPref } from "@/lib/stepper-preference";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

const OPENROUTER_MAX_PRICE_ERROR_MARKER =
  "No endpoints found that satisfy the max price for this request";
const REASONING_ROUTER_UNAVAILABLE_MESSAGE =
  "No se pudo determinar automáticamente el nivel de razonamiento; se continuó con nivel bajo. Puedes reintentar o elegir un nivel manual.";

interface StepMeta {
  label: string;
  icon: IconType;
}

const STEP_META: Record<string, StepMeta> = {
  retrieval: { label: "retrieval", icon: Search },
  "retrieval.embedding": { label: "embedding", icon: Compass },
  "retrieval.vector": { label: "vector", icon: Compass },
  "retrieval.bm25": { label: "okapi bm25", icon: Type },
  "retrieval.sparse": { label: "sparse", icon: Braces },
  "retrieval.graph": { label: "graph", icon: Network },
  "retrieval.graph_routing": { label: "ruteo de grafo", icon: GitBranch },
  "retrieval.entity_linking": { label: "vinculando entidades", icon: Link2 },
  "retrieval.fusion": { label: "fusionando candidatos", icon: Combine },
  "retrieval.metadata": { label: "enriqueciendo metadata", icon: Tags },
  rerank: { label: "rerank", icon: ArrowUpDown },
  question_classification: { label: "clasificando pregunta", icon: Tags },
  reasoning_router: { label: "evaluando razonamiento", icon: Gauge },
  model_routing: { label: "enrutando modelo", icon: Route },
  "retrieval.skipped": { label: "retrieval omitido", icon: SkipForward },
  thinking: { label: "razonando", icon: Brain },
  wrapup: { label: "cerrando respuesta", icon: CircleCheck },
  answer: { label: "respondiendo", icon: MessageSquare },
  fact_capture: { label: "evaluando aprendizaje", icon: BookmarkPlus },
};

function resolveToolMeta(id: string): StepMeta {
  const name = id.slice("tool.".length);
  if (name.startsWith("github_")) return { label: name, icon: GitBranch };
  if (name.startsWith("bookmark_")) return { label: name, icon: Bookmark };
  if (name.startsWith("workflow_")) return { label: name, icon: Workflow };
  return { label: name, icon: Database };
}

function metaFor(id: string): StepMeta {
  if (id.startsWith("tool.")) return resolveToolMeta(id);
  return STEP_META[id] ?? { label: id, icon: Database };
}

// Canonical pipeline-phase order for rendering. The persisted snapshot
// (`metadata.steps`) is in done-emit order — for the rehydrated view that
// means sub-retrievers appear before their parent `retrieval` because their
// `done` events fire first inside `asyncio.gather`. Sorting at render time
// gives a stable, operator-friendly tree (parent → children) for both live
// and rehydrated turns.
const STEP_PHASE_ORDER: Record<string, number> = {
  question_classification: -1,
  reasoning_router: 0,
  model_routing: 1,
  retrieval: 2,
  rerank: 3,
  thinking: 4,
  wrapup: 5,
  answer: 6,
  fact_capture: 7,
};

function phaseOf(stepId: string): string {
  // Tools are visually nested under the `answer` phase even though their
  // id has a virtual `tool` parent.
  if (stepId.startsWith("tool.")) return "answer";
  if (stepId.includes(".")) return stepId.split(".")[0] ?? stepId;
  return stepId;
}

// The raw-query BM25 pass (`retrieval.bm25_raw`) is a supplementary second
// lexical pass over the un-condensed message (multi-turn only). It is folded
// into the single `okapi bm25` row rather than shown as a redundant sibling,
// so it is filtered out of every display surface (tree + ticker). The pass
// still runs and contributes to fusion; only the separate step row is hidden.
const FOLDED_RETRIEVAL_STEP_ID = "retrieval.bm25_raw";

function reorderForDisplay(steps: StepState[]): StepState[] {
  return steps
    .filter((step) => step.id !== FOLDED_RETRIEVAL_STEP_ID)
    .map((step, idx) => ({ step, idx }))
    .sort((a, b) => {
      const pa = STEP_PHASE_ORDER[phaseOf(a.step.id)] ?? 99;
      const pb = STEP_PHASE_ORDER[phaseOf(b.step.id)] ?? 99;
      if (pa !== pb) return pa - pb;
      // Parent (no dot) before children (dot present) within a phase.
      const da = a.step.id.includes(".") ? 1 : 0;
      const db = b.step.id.includes(".") ? 1 : 0;
      if (da !== db) return da - db;
      // Preserve original order among same (phase, depth).
      return a.idx - b.idx;
    })
    .map(({ step }) => step);
}

function formatElapsed(ms: number | undefined): string {
  if (ms === undefined) return "";
  // Sub-millisecond tools (e.g. `render_diagram`, which is just an
  // in-process shape check with no I/O) round to 0ms via `int(...)`
  // on the backend. Showing a bare "0ms" reads like a bug; `<1ms`
  // makes the "instantaneous" reality explicit.
  if (ms === 0) return "<1ms";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** Header-chip total: floored whole seconds under a minute, `Nm Ns`
 * once we cross 60s — matches Claude Code's tool-call timer
 * ("2m 36s") so a long turn doesn't render as a giant raw second
 * count. Used by both the live `PipelineSteps` header and the
 * post-stream `DetailsPanel` chip so the live and rest positions
 * read the same way. */
export function formatTotalElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec} s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s}s`;
}

const _tokenFmt = new Intl.NumberFormat("en-US");

/** Render a USD amount for a step-usage line: `$?` when pricing is
 * unknown, `<$0.0001` for sub-tenth-of-a-cent costs, otherwise a
 * 4-decimal dollar figure. */
function formatUsd(costUsd: number | null, costSource: "computed" | "unknown"): string {
  if (costSource === "unknown" || costUsd === null) return "$?";
  if (costUsd > 0 && costUsd < 0.0001) return "<$0.0001";
  return `$${costUsd.toFixed(4)}`;
}

/** One-line per-step usage label. Standard LLM steps render
 * `model · N in · M out · $usd`; rerank-shape steps (zero prompt and
 * completion tokens) render `model · N units · $usd`. */
export function formatStepUsage(usage: StepUsage): string {
  const usd = formatUsd(usage.costUsd, usage.costSource);
  // Prefix the model-settings slot the model was resolved from (e.g.
  // `utility · …`). Historical rows persisted before slot tracking have no
  // slot, so the line starts at the model.
  const prefix = usage.slot ? `${usage.slot} · ` : "";
  if (usage.promptTokens === 0 && usage.completionTokens === 0 && usage.totalTokens > 0) {
    return `${prefix}${usage.model} · ${_tokenFmt.format(usage.totalTokens)} units · ${usd}`;
  }
  return `${prefix}${usage.model} · ${_tokenFmt.format(usage.promptTokens)} in · ${_tokenFmt.format(
    usage.completionTokens,
  )} out · ${usd}`;
}

/** Closed Spanish table for a decision-engine call's `reason`; an unknown id
 * renders as itself. */
export const CALL_REASON_LABELS: Record<string, string> = {
  low_confidence: "baja confianza",
  timeout: "tiempo agotado",
  rate_limited: "límite de tasa",
  overloaded: "servicio saturado",
  server_error: "error del servidor",
  auth: "credencial rechazada",
  validation: "solicitud rechazada",
  transport: "error de red",
  malformed_response: "respuesta inválida",
  no_api_key: "sin credencial", // pragma: allowlist secret
};

const NO_RESULT = "sin resultado";

const callLabel = (call: StepCall): string => call.label ?? call.model;
const callReason = (reason: string): string => CALL_REASON_LABELS[reason] ?? reason;
const callCost = (call: StepCall): string =>
  call.costUsd === null ? "$?" : formatCost(call.costUsd);
const callLatency = (call: StepCall): string[] =>
  call.latencyMs === null ? [] : [`${call.latencyMs} ms`];

/** Chip parts summarising the deciding path of a step: the first engine call
 * either served (`stats`) or not (`reason`, plus the later served call). Null
 * when no decision-engine call took part. */
export function describeCalls(
  calls: StepCall[],
): { label: string; stats?: string; reason?: string; served?: string } | null {
  const engine = calls.find((c) => c.kind === "decision_engine");
  if (engine === undefined) return null;
  const label = callLabel(engine);
  if (engine.outcome === "served") {
    return { label, stats: [...callLatency(engine), callCost(engine)].join(" · ") };
  }
  // An engine call without an outcome never finished (e.g. the step timed out
  // after the call was recorded), so it did not decide.
  const reason = engine.outcome === undefined ? NO_RESULT : callReason(engine.reason ?? "");
  const served = calls.find((c) => c !== engine && c.outcome === "served");
  return { label, reason, ...(served ? { served: callLabel(served) } : {}) };
}

function callOutcomeText(call: StepCall): string | null {
  if (call.outcome === "served") return "sirvió";
  const reason = call.reason ? `: ${callReason(call.reason)}` : "";
  if (call.outcome === "fell_through") return `cayó al siguiente${reason}`;
  if (call.outcome === "failed") return `falló${reason}`;
  if (call.kind === "decision_engine") return NO_RESULT;
  return null;
}

/** `respuesta: level low · needs_retrieval sí` — each question id (raw, like
 * model ids in this debug panel) with its decided value, booleans as `sí` /
 * `no`. Null when the call carries no answer. */
function callAnswerText(call: StepCall): string | null {
  const entries = Object.entries(call.answer ?? {});
  if (entries.length === 0) return null;
  const pairs = entries.map(
    ([id, value]) => `${id} ${typeof value === "boolean" ? (value ? "sí" : "no") : value}`,
  );
  return `respuesta: ${pairs.join(" · ")}`;
}

function formatCallLine(call: StepCall): string {
  return [
    callLabel(call),
    ...callLatency(call),
    `${_tokenFmt.format(call.promptTokens)} in`,
    `${_tokenFmt.format(call.completionTokens)} out`,
    callCost(call),
    callOutcomeText(call),
    callAnswerText(call),
  ]
    .filter((p): p is string => p !== null)
    .join(" · ");
}

function CallChip({ calls }: { calls: StepCall[] }): JSX.Element | null {
  const d = describeCalls(calls);
  if (d === null) return null;
  return (
    <span
      data-testid="call-chip"
      className="max-w-full break-words rounded-sm bg-muted px-1 py-px text-muted-foreground"
    >
      {d.reason === undefined ? (
        `${d.label} · ${d.stats}`
      ) : (
        <>
          {d.label} <span aria-hidden="true">✗</span> {d.reason}
          {d.served !== undefined && ` → ${d.served}`}
        </>
      )}
    </span>
  );
}

interface PipelineStepsProps {
  steps: StepState[];
  /**
   * True while the SSE stream is in flight. Distinct from "any step is
   * `running`" because the backend buffers events and drains them in bursts
   * — between drains the FE can momentarily see all steps as `done` while
   * the stream is still very much in flight. Using an explicit prop keeps
   * the stepper open during those gaps and lets us render a placeholder
   * before the first step event arrives (the Supabase round-trips that
   * precede `reasoning_router:start` can add ~1 s).
   */
  streaming: boolean;
  citationCount: number;
  /** Tool dispatches recorded for this turn. When passed, each `tool.*`
   * step row matches the N-th call with the same name (steps and
   * `toolCalls` are both populated in dispatch order) and the row
   * becomes an expandable `<details>` carrying target/ref/body. */
  toolCalls?: ToolCall[];
  /** `performance.now()` snapshot taken when the user submitted, so
   * the live counter can start ticking from "0 s" the instant the
   * stepper mounts — before the first SSE `:start` event arrives.
   * Preferred over the per-step `startedAt` minimum when present. */
  turnStartedAt?: number;
  /** Resolved effort level for the turn (e.g. `low`, `medium`). Surfaced
   * as a chip next to the `eval effort` row, styled like retrieval count
   * pills, so the operator sees which gear the router picked. */
  reasoningLevel?: ReasoningLevel;
}

export function PipelineSteps({
  steps,
  streaming,
  citationCount,
  toolCalls,
  turnStartedAt,
  reasoningLevel,
}: PipelineStepsProps): JSX.Element | null {
  // Sticky open/closed: initialize from the persisted per-browser preference
  // (default collapsed). Each new turn mounts a fresh `PipelineSteps`, so it
  // picks up the latest persisted value; toggling persists immediately so the
  // *next* turn starts the same way.
  const [expanded, setExpanded] = useState<boolean>(readStepperExpandedPref);
  const toggleExpanded = (next: boolean): void => {
    setExpanded(next);
    writeStepperExpandedPref(next);
  };

  // Tick once a second while the stream is in flight so the header
  // chip and the running-row elapsed counters advance live, the way
  // Claude Code's tool-call timer does ("12s" → "13s" → …). State
  // value itself is unused — the re-render is the point. Cleared as
  // soon as `streaming` flips to false so we don't churn the React
  // tree after the answer is done.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!streaming) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [streaming]);

  // Bounded scroll viewport for the expanded tree. Long turns (many tool
  // calls) would otherwise grow the stepper unbounded, pushing the answer
  // off-screen and never scrolling the newest rows into view. The container
  // is capped at `max-h-[40vh]` and auto-follows the latest row using the
  // same bottom-follow behavior as the chat transcript: following pauses
  // when the operator scrolls up past a 24px threshold and resumes near the
  // bottom.
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoFollowRef = useRef(true);

  // Re-attach the scroll listener whenever the expanded container mounts
  // (it's absent while collapsed). A programmatic pin-to-bottom leaves us
  // near the bottom, so the handler keeps follow on — no ignore flag needed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `expanded` is intentional — the container only exists while expanded, so the effect must re-run on toggle to (re)attach the listener.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      autoFollowRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 24;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [expanded]);

  // Pin the viewport to the bottom as new step events arrive, but only
  // while the operator hasn't scrolled up to inspect an earlier step.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `expanded` is intentional — re-pin to the newest row when the operator re-expands a long stepper, even if `steps` didn't change.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !autoFollowRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [steps, expanded]);

  // No steps yet AND not streaming → nothing worth rendering (e.g. a
  // turn that errored before any step fired). Streaming with zero
  // steps falls through to the live render: when collapsed the ticker
  // shows a placeholder, when expanded the header chip ticks from
  // `turnStartedAt` and the body shows the transition spinner until
  // the first step row lands.
  if (steps.length === 0 && !streaming) return null;

  // While the stream is live we want a monotonic wall-clock that ticks
  // up second-by-second and never goes backward. The previous "sum of
  // top-level steps, swap running step's `elapsedMs` for `now -
  // startedAt`" approach mixed two clocks:
  //
  //   * `now - startedAt_frontend` includes SSE delivery latency for the
  //     `:start` event.
  //   * `elapsedMs` from the `:done` event is the backend's own
  //     `time.monotonic()` delta, with no network latency baked in.
  //
  // When a step flips running → done the chip would lurch up or down by
  // the size of that latency, sometimes producing a literal decrement.
  //
  // Use the frontend's own clock for the live total instead — the
  // earliest `startedAt` we've seen across all steps anchors the turn,
  // and `now - that` only ever grows. Once `streaming` flips off, fall
  // back to the backend-precise sum (post-stream is where operators
  // care about exact processing time, and there's no live-tick worry).
  const stepIds = new Set(steps.map((s) => s.id));
  const now = performance.now();
  // Prefer the explicit turn anchor (set by `chat.tsx` the instant
  // the user submitted) so the chip can tick from "0 s" before any
  // step event arrives. Fall back to the earliest per-step
  // `startedAt` for callers that don't supply a turn anchor.
  // Persisted steps land with `startedAt: 0`; ignore those so a stale
  // "0" doesn't dwarf real frontend timestamps.
  const liveAnchor: number | null = (() => {
    if (!streaming) return null;
    if (turnStartedAt !== undefined) return turnStartedAt;
    const valid = steps.map((s) => s.startedAt).filter((t) => t > 0);
    if (valid.length === 0) return null;
    return Math.min(...valid);
  })();
  const liveTotalMs = liveAnchor !== null ? Math.max(0, now - liveAnchor) : null;
  const backendTotalMs = steps
    .filter((s) => {
      const parent = parentOf(s.id);
      return parent === null || !stepIds.has(parent);
    })
    .reduce((acc, s) => acc + (s.elapsedMs ?? 0), 0);
  const totalMs = liveTotalMs ?? backendTotalMs;

  if (!expanded) {
    // While the stream is in flight, the collapsed state is a live activity
    // ticker showing the current (or most recently completed) pipeline step.
    // Once the stream is done it falls back to the static summary chip.
    if (streaming) {
      return (
        <ActivityTicker steps={steps} totalMs={totalMs} onExpand={() => toggleExpanded(true)} />
      );
    }
    return (
      <button
        type="button"
        onClick={() => toggleExpanded(true)}
        className="mt-3 inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-label-2 text-muted-foreground hover:bg-black/[0.025] dark:hover:bg-white/[0.025]"
      >
        <span>
          ▸ steps · {formatTotalElapsed(totalMs)} · {citationCount} sources
        </span>
      </button>
    );
  }

  return (
    <div className="mt-3 flex flex-col gap-0.5 text-label-2">
      <button
        type="button"
        onClick={() => toggleExpanded(false)}
        aria-label="hide steps"
        className="mb-0.5 inline-flex w-fit items-center gap-1 rounded px-1 py-px text-muted-foreground hover:bg-black/[0.025] hover:text-foreground dark:hover:bg-white/[0.025]"
      >
        <span>
          ▾ steps · {formatTotalElapsed(totalMs)} · {citationCount} sources
        </span>
      </button>
      <div
        ref={scrollRef}
        data-testid="stepper-scroll"
        // `scrollbar-gutter: stable` reserves the scrollbar's width whether or
        // not the overflow scrollbar is showing, so the right-aligned elapsed
        // times don't jump left the moment the tree grows past `max-h-[40vh]`
        // and the scrollbar appears.
        className="flex max-h-[40vh] flex-col gap-0.5 overflow-y-auto [scrollbar-gutter:stable]"
      >
        <StepsTree
          steps={steps}
          streaming={streaming}
          {...(toolCalls ? { toolCalls } : {})}
          {...(reasoningLevel ? { reasoningLevel } : {})}
        />
      </div>
    </div>
  );
}

/** Flatten a step to a one-line ticker segment: its label plus the most
 * relevant inline detail (sub-retriever hits, RRF fused count, graph-routing
 * decision). Mirrors the chips the expanded tree shows for that step so the
 * collapsed view stays legible at a glance. */
function tickerSegment(step: StepState): string {
  const { label } = metaFor(step.id);
  const detail = step.detail;
  if (detail) {
    if (step.id.startsWith("retrieval.") && detail.hits !== undefined) {
      return `${label} ${detail.hits}`;
    }
    if (step.id === "retrieval" && detail.fused !== undefined) {
      return `${label} fused ${detail.fused}`;
    }
    if (step.id === "retrieval.graph_routing" && typeof detail.decision === "string") {
      return `${label} ${detail.decision}`;
    }
    if (step.id === "model_routing") {
      const parts: string[] = [];
      if (typeof detail.tier === "string") parts.push(detail.tier);
      const selected = detail.selected;
      if (
        selected &&
        typeof selected === "object" &&
        "model_id" in selected &&
        typeof (selected as { model_id: unknown }).model_id === "string"
      ) {
        parts.push((selected as { model_id: string }).model_id);
      }
      if (typeof detail.reason === "string") parts.push(detail.reason);
      return parts.length > 0 ? `${label} ${parts.join(" · ")}` : label;
    }
    if (step.id === "retrieval.skipped" && typeof detail.reason === "string") {
      return `${label} ${detail.reason}`;
    }
  }
  return label;
}

/** The furthest-along completed top-level step, used as the ticker's fallback
 * during the gap between phases (when nothing is `running` but the stream is
 * still in flight). Sub-steps (dotted ids) and tool steps are excluded — the
 * phase markers are what read meaningfully on their own. */
function lastCompletedTopLevel(steps: StepState[]): StepState | undefined {
  const done = steps.filter(
    (s) => !s.id.includes(".") && (s.status === "done" || s.status === "error"),
  );
  if (done.length === 0) return undefined;
  return done.reduce((best, s) => {
    const ps = STEP_PHASE_ORDER[phaseOf(s.id)] ?? 99;
    const pb = STEP_PHASE_ORDER[phaseOf(best.id)] ?? 99;
    return ps >= pb ? s : best;
  });
}

/** Compute the ticker's current line. Returns `null` before the first step
 * event (caller renders a neutral placeholder). While steps are `running`,
 * shows them as `·`-separated segments; running children are grouped under
 * their phase's running parent label (`retrieval — vector 30 · bm25 30 · graph
 * 8`, or `respondiendo — github_read_file` for a tool dispatched mid-answer).
 * In the gap between phases, falls back to the last completed top-level step. */
function tickerContent(allSteps: StepState[]): string | null {
  const steps = allSteps.filter((s) => s.id !== FOLDED_RETRIEVAL_STEP_ID);
  const running = steps.filter((s) => s.status === "running");
  if (running.length > 0) {
    const runningChildren = running.filter((s) => s.id.includes("."));
    if (runningChildren.length > 0) {
      // Nest children under the running top-level step of the same phase. This
      // uses `phaseOf` (not `parentOf`) so `tool.*` steps — whose literal
      // parent id is the virtual `tool` — group under the real `answer` step
      // instead of rendering a bare "tool" prefix.
      const phase = phaseOf(runningChildren[0]?.id ?? "");
      const parentStep = running.find((s) => !s.id.includes(".") && phaseOf(s.id) === phase);
      const segments = runningChildren.map(tickerSegment).join(" · ");
      return parentStep ? `${metaFor(parentStep.id).label} — ${segments}` : segments;
    }
    return running.map(tickerSegment).join(" · ");
  }
  const lastDone = lastCompletedTopLevel(steps);
  return lastDone ? tickerSegment(lastDone) : null;
}

/** Collapsed-while-streaming view: a one-line activity ticker. Bigger than the
 * post-stream summary chip, it surfaces what the pipeline is doing right now —
 * the running step(s) with their counts, or the last completed phase during a
 * gap. The content node is keyed by its text so a change remounts it, replaying
 * the `ticker-in` entry animation. Clicking anywhere expands the full tree. */
function ActivityTicker({
  steps,
  totalMs,
  onExpand,
}: {
  steps: StepState[];
  totalMs: number;
  onExpand: () => void;
}): JSX.Element {
  const content = tickerContent(steps);
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label="show steps"
      data-testid="stepper-ticker"
      className="mt-3 flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-label-2 text-muted-foreground hover:bg-black/[0.025] dark:hover:bg-white/[0.025]"
    >
      <LoaderCircle className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
      <span
        // Keyed by content so React remounts (not just re-renders) the node on
        // every change, restarting the entry animation. A second-by-second
        // elapsed tick with unchanged content keeps the same key → no replay.
        key={content ?? "__placeholder__"}
        data-testid="ticker-content"
        className="min-w-0 flex-1 animate-ticker-in truncate text-left text-foreground"
      >
        {content ?? "preparando…"}
      </span>
      <span className="shrink-0 tabular-nums">{formatTotalElapsed(totalMs)}</span>
      <span aria-hidden="true" className="shrink-0">
        ▸
      </span>
    </button>
  );
}

/** Renders the step-row tree (eval effort → retrieval → … → eval
 * learning) without any surrounding chip or wrapper div, so callers
 * that want their own header — e.g. the post-stream `DetailsPanel`
 * which folds steps under a `▸ details` summary — can compose freely.
 * `PipelineSteps` (the standalone stepper rendered above the composer
 * during streaming) uses this internally too. */
export function StepsTree({
  steps,
  streaming,
  toolCalls,
  reasoningLevel,
}: {
  steps: StepState[];
  streaming: boolean;
  /** Optional tool-dispatch payloads keyed by name + order. When the
   * caller passes them, each `tool.*` step row becomes an expandable
   * `<details>` whose body carries target / ref / preview — the
   * detail that used to live in the now-removed inline
   * `ToolCallBlock`s above the answer. */
  toolCalls?: ToolCall[];
  /** When provided, appended as a count-style chip on the
   * `reasoning_router` row so the operator can see the picked effort
   * level inline with the rest of the step tree. */
  reasoningLevel?: ReasoningLevel;
}): JSX.Element {
  const anyRunning = steps.some((s) => s.status === "running");
  const sortedSteps = reorderForDisplay(steps);

  // Match each `tool.*` step row to its corresponding `ToolCall`
  // payload by (name, order). Both the SSE-driven reducer and the
  // hydration path preserve dispatch order in `toolCalls`, and tool
  // steps in `sortedSteps` keep their original index thanks to the
  // tie-breaker in `reorderForDisplay`, so the N-th `tool.foo` step
  // pairs with the N-th `ToolCall` whose `name === "foo"`. Backend
  // never persists a `tool_use_id` on the step event, so name+order
  // is the only stable join we have today.
  const stepToToolCall = new Map<number, ToolCall>();
  if (toolCalls && toolCalls.length > 0) {
    const counters: Record<string, number> = {};
    sortedSteps.forEach((step, idx) => {
      if (!step.id.startsWith("tool.")) return;
      const name = step.id.slice("tool.".length);
      const n = counters[name] ?? 0;
      counters[name] = n + 1;
      const matched = toolCalls.filter((tc) => tc.name === name)[n];
      if (matched) stepToToolCall.set(idx, matched);
    });
  }

  return (
    <>
      {sortedSteps.map((step, idx) => {
        const parent = parentOf(step.id);
        const isToolStep = step.id.startsWith("tool.");
        const indented = parent !== null;
        const meta = metaFor(step.id);
        const Icon = meta.icon;
        const tool = stepToToolCall.get(idx);
        // Tools can repeat the same id within a turn (multiple
        // dispatches), so the id alone isn't unique. Use the index
        // suffix to keep React keys stable per row.
        const rowKey = `${step.id}#${idx}`;
        if (tool) {
          return (
            <ToolStepRow key={rowKey} step={step} tool={tool} Icon={Icon} label={meta.label} />
          );
        }
        // LLM-bearing steps carry `usage`; render them as an expandable
        // row whose body is the model / tokens / USD detail line.
        if (step.usage !== undefined) {
          return (
            <UsageStepRow
              key={rowKey}
              step={step}
              Icon={Icon}
              label={meta.label}
              indented={indented || isToolStep}
              {...(step.id === "reasoning_router" && reasoningLevel ? { reasoningLevel } : {})}
            />
          );
        }
        return (
          <div
            key={rowKey}
            className={
              indented || isToolStep
                ? "ml-4 flex items-center gap-2 border-l-2 border-brand-primary/40 pl-2"
                : "flex items-center gap-2"
            }
            aria-busy={step.status === "running"}
          >
            <StatusIcon status={step.status} />
            <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <span className="text-foreground">{meta.label}</span>
            {renderDetailChips(step)}
            {step.id === "reasoning_router" && reasoningLevel && (
              <span className="rounded-sm bg-muted px-1 py-px text-muted-foreground">
                {reasoningLevel}
              </span>
            )}
            {step.elapsedMs !== undefined && (
              <span className="ml-auto text-muted-foreground">{formatElapsed(step.elapsedMs)}</span>
            )}
          </div>
        );
      })}
      {/* Transition spinner — covers the gap between a phase finishing
       * (eg. `rerank:done`) and the next one starting (eg.
       * `thinking:start`). Without it, every visible row carries a
       * green check and the tree reads as "finished" while the LLM is
       * actually still pre-loading the next phase. Only renders while
       * the SSE stream is in flight; the rest-position panel always
       * passes `streaming={false}`. */}
      {streaming && !anyRunning && (
        <div className="flex items-center gap-2" aria-busy="true">
          <LoaderCircle
            className="h-3.5 w-3.5 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
          <span className="italic text-muted-foreground">…</span>
        </div>
      )}
    </>
  );
}

/** LLM-bearing step rows fold the model / tokens / USD detail under a
 * `<details>` so a click reveals the cost without cluttering the
 * default tree view. `open` is uncontrolled — multiple rows may be
 * open at once, matching `ToolStepRow`. */
function UsageStepRow({
  step,
  Icon,
  label,
  indented,
  reasoningLevel,
}: {
  step: StepState;
  Icon: IconType;
  label: string;
  indented: boolean;
  reasoningLevel?: ReasoningLevel;
}): JSX.Element {
  return (
    <details className={indented ? "ml-4 border-l-2 border-brand-primary/40 pl-2" : undefined}>
      <summary className="flex cursor-pointer list-none items-center gap-2">
        <StatusIcon status={step.status} />
        <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        {/* Underline marks the row as clickable for its usage detail — the
         * affordance is otherwise invisible until hover/click. Dotted +
         * offset keeps it subtle so it doesn't read as a link. */}
        <span className="text-foreground underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
          {label}
        </span>
        {renderDetailChips(step)}
        {step.usage?.calls && <CallChip calls={step.usage.calls} />}
        {step.id === "reasoning_router" && reasoningLevel && (
          <span className="rounded-sm bg-muted px-1 py-px text-muted-foreground">
            {reasoningLevel}
          </span>
        )}
        {step.elapsedMs !== undefined && (
          <span className="ml-auto text-muted-foreground">{formatElapsed(step.elapsedMs)}</span>
        )}
      </summary>
      {step.usage !== undefined && (
        <div className="ml-6 border-l-2 border-brand-primary/40 pl-2 text-muted-foreground">
          {formatStepUsage(step.usage)}
          {step.usage.calls?.map((call, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: calls are an ordered, immutable list
            <div key={i} data-testid="call-line" className="break-words">
              {formatCallLine(call)}
            </div>
          ))}
        </div>
      )}
    </details>
  );
}

/** Tool step rows fold the tool-call payload (target, ref, content
 * preview, error message) under a `<details>` so the conversation
 * doesn't carry redundant tool blocks above the answer. The
 * `<summary>` keeps the same row layout as a regular step; the
 * disclosure caret on the left tells the operator it's expandable. */
function ToolStepRow({
  step,
  tool,
  Icon,
  label,
}: {
  step: StepState;
  tool: ToolCall;
  Icon: IconType;
  label: string;
}): JSX.Element {
  const display = describeTool(tool);
  const hasBody = !!(display.target || display.refText || tool.content_preview || tool.error);
  return (
    <details
      // `open` is intentionally uncontrolled — operators may want
      // several tool rows open at once to compare payloads.
      className="ml-4 border-l-2 border-brand-primary/40 pl-2"
    >
      <summary
        className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden"
        aria-busy={step.status === "running"}
      >
        <span className="text-muted-foreground transition-transform group-open:rotate-90 [details[open]_&]:rotate-90">
          ▸
        </span>
        <StatusIcon status={step.status} />
        <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <span className="text-foreground">{label}</span>
        {renderDetailChips(step)}
        {step.elapsedMs !== undefined && (
          <span className="ml-auto text-muted-foreground">{formatElapsed(step.elapsedMs)}</span>
        )}
      </summary>
      {hasBody && (
        <div className="mt-1 ml-6 space-y-1 text-muted-foreground">
          {display.target && (
            <div className="break-all font-mono text-body-3">{display.target}</div>
          )}
          {display.refText && <div className="italic">{display.refText}</div>}
          {tool.ok === false && tool.error && (
            <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-muted px-2 py-1 text-body-3 text-destructive">
              {tool.error}
            </pre>
          )}
          {tool.ok !== false && tool.content_preview && (
            <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded bg-muted px-2 py-1 font-mono text-body-3">
              {tool.content_preview}
            </pre>
          )}
        </div>
      )}
    </details>
  );
}

function StatusIcon({ status }: { status: StepState["status"] }): JSX.Element {
  if (status === "running") {
    return (
      <LoaderCircle className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
    );
  }
  if (status === "done") {
    return <CircleCheck className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" />;
  }
  return <CircleAlert className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />;
}

function renderDetailChips(step: StepState): JSX.Element | null {
  const detail = step.detail;
  if (!detail) return null;
  // Graph routing decision: surface whether the classifier ran or skipped the
  // graph (the spec's diagnosability goal — the row otherwise looks identical
  // either way). The classifier's reason rides along as the hover title.
  if (step.id === "retrieval.graph_routing" && typeof detail.decision === "string") {
    const ran = detail.decision === "ran";
    return (
      <span
        className="rounded-sm bg-muted px-1 py-px text-muted-foreground"
        title={typeof detail.reason === "string" ? detail.reason : undefined}
      >
        {ran ? "ran" : "skipped"}
      </span>
    );
  }
  if (step.id === "question_classification" && typeof detail.intent === "string") {
    const label = detail.intent === "meta" ? "meta" : "usuario";
    return (
      <span
        data-testid="intent-chip"
        className="rounded-sm bg-muted px-1 py-px text-muted-foreground"
      >
        {detail.decided_by === "default" ? `${label} · por defecto` : label}
      </span>
    );
  }
  // The sub-retriever id (`retrieval.vector` / `.bm25` / `.graph`) is
  // already in the row label, so the hits chip just needs the count —
  // the `v·` / `b·` / `g·` prefixes that used to disambiguate are
  // redundant now that the label itself reads "vector" / "bm25" /
  // "graph".
  if (step.id.startsWith("retrieval.") && detail.hits !== undefined) {
    return (
      <span className="rounded-sm bg-muted px-1 py-px text-muted-foreground">{detail.hits}</span>
    );
  }
  // Parent `retrieval` row carries the RRF output size — the bridge
  // between the parallel sub-retrievers and rerank. Reads as the
  // funnel: 3×N hits → fused M → rerank. Labelled (`fused N`)
  // because the bare number reads as a count without context next to
  // the sub-rows that already carry naked counts.
  if (step.id === "retrieval" && detail.fused !== undefined) {
    return (
      <span className="rounded-sm bg-muted px-1 py-px text-muted-foreground">
        fused {detail.fused}
      </span>
    );
  }
  if (step.id === "model_routing") {
    return renderModelRoutingDetailChips(detail);
  }
  if (step.id === "retrieval.skipped" && typeof detail.reason === "string") {
    return (
      <span className="rounded-sm bg-muted px-1 py-px text-muted-foreground" title={detail.reason}>
        {detail.reason}
      </span>
    );
  }
  if (step.id.startsWith("tool.") && (detail.bytes !== undefined || detail.stage !== undefined)) {
    const chips: JSX.Element[] = [];
    if (detail.stage !== undefined) {
      // Distinguishable from the bytes chip's neutral `bg-muted` — the info
      // alert colors (same tokens `Badge variant="info"` uses) so the
      // resolved data-source stage reads as a separate signal at a glance.
      chips.push(
        <span
          key="stage"
          className="rounded-sm bg-brand-alert-info-00 px-1 py-px text-brand-alert-info-10"
        >
          {detail.stage}
        </span>,
      );
    }
    if (detail.bytes !== undefined) {
      const display =
        detail.bytes < 1024 ? `${detail.bytes} B` : `${(detail.bytes / 1024).toFixed(1)} kB`;
      chips.push(
        <span key="bytes" className="rounded-sm bg-muted px-1 py-px text-muted-foreground">
          {display}
        </span>,
      );
    }
    return <span className="inline-flex flex-wrap items-center gap-1">{chips}</span>;
  }
  if (step.status === "error" && typeof detail.error === "string") {
    const errorMessage =
      step.id === "reasoning_router" && detail.error.includes(OPENROUTER_MAX_PRICE_ERROR_MARKER)
        ? REASONING_ROUTER_UNAVAILABLE_MESSAGE
        : detail.error;
    return (
      <span className="rounded-sm border border-amber-500/40 bg-amber-50 px-1 py-px text-amber-700 dark:bg-amber-950/20">
        {errorMessage}
      </span>
    );
  }
  return null;
}

function renderModelRoutingDetailChips(
  detail: NonNullable<StepState["detail"]>,
): JSX.Element | null {
  const chips: JSX.Element[] = [];
  if (typeof detail.tier === "string") {
    chips.push(
      <span key="tier" className="rounded-sm bg-muted px-1 py-px text-muted-foreground">
        {detail.tier}
      </span>,
    );
  }
  const selected = detail.selected;
  if (
    selected &&
    typeof selected === "object" &&
    "provider" in selected &&
    "model_id" in selected &&
    typeof (selected as { provider: unknown }).provider === "string" &&
    typeof (selected as { model_id: unknown }).model_id === "string"
  ) {
    const sel = selected as { provider: string; model_id: string };
    chips.push(
      <span
        key="selected"
        className="rounded-sm bg-muted px-1 py-px font-mono text-muted-foreground"
        title={sel.provider}
      >
        {sel.model_id}
      </span>,
    );
  }
  if (typeof detail.reason === "string") {
    chips.push(
      <span
        key="reason"
        className="rounded-sm bg-muted px-1 py-px text-muted-foreground"
        title="motivo"
      >
        {detail.reason}
      </span>,
    );
  }
  if (detail.shadow === true) {
    chips.push(
      <span key="shadow" className="rounded-sm bg-muted px-1 py-px text-muted-foreground">
        sombra
      </span>,
    );
  }
  if (chips.length === 0) return null;
  return <span className="inline-flex flex-wrap items-center gap-1">{chips}</span>;
}
